// src/components/RecommendationEngine.js
/**
 * AI Recommendation Engine Component
 * Personalized beverage recommendations based on user preferences and learning
 */

import React, { useState, useEffect } from "react";
import { useRestaurant } from "../contexts/RestaurantContext";
import { advancedRecommendationEngine } from "../services/advancedRecommendationEngine";
import PropTypes from "prop-types";
import "./RecommendationEngine.css";

const RecommendationEngine = ({
  inventory = [],
  compact = false,
  maxRecommendations = 8,
  showPersonalization = true,
  onRecommendationSelect = null,
}) => {
  const { currentRestaurant } = useRestaurant();
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userPreferences, setUserPreferences] = useState({
    flavors: [],
    categories: [],
    occasions: [],
    priceRange: { min: "", max: "" },
    experienceLevel: "intermediate",
    personalNotes: "",
  });
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [feedbackGiven, setFeedbackGiven] = useState(new Set());

  const recommendationEngine = advancedRecommendationEngine;

  // Load initial recommendations on mount
  useEffect(() => {
    if (inventory.length > 0) {
      generateRecommendations();
    }
  }, [inventory]);

  const generateRecommendations = async (preferences = userPreferences) => {
    try {
      setLoading(true);
      setError(null);

      // Build context for recommendations
      const context = {
        restaurant: currentRestaurant?.name,
        occasion: "general",
        timeOfDay: getTimeOfDay(),
        season: getSeason(),
        maxRecommendations,
      };

      // Get personalized recommendations
      const result = await recommendationEngine.getPersonalizedRecommendations(
        preferences,
        inventory,
        context
      );

      setRecommendations(result);
    } catch (err) {
      console.error("Recommendation error:", err);
      setError("Failed to generate recommendations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceUpdate = (newPreferences) => {
    setUserPreferences(newPreferences);
    generateRecommendations(newPreferences);
    setShowPreferencesModal(false);
  };

  const handleRecommendationClick = (recommendation) => {
    setSelectedRecommendation(recommendation);
    if (onRecommendationSelect) {
      onRecommendationSelect(recommendation);
    }
  };

  const handleFeedback = async (recommendation, feedback) => {
    try {
      // Simulate user interaction learning
      const interaction = {
        type: feedback,
        beverage: recommendation,
        timestamp: Date.now(),
        context: userPreferences,
      };

      // Learn from interaction (would normally use user ID)
      await recommendationEngine.learnFromInteraction(
        "current-user",
        interaction
      );

      // Mark feedback as given
      setFeedbackGiven((prev) => new Set([...prev, recommendation.id]));

      // Regenerate recommendations with new learning
      generateRecommendations();
    } catch (error) {
      console.error("Feedback error:", error);
    }
  };

  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "morning";
    if (hour < 17) return "afternoon";
    if (hour < 21) return "evening";
    return "night";
  };

  const getSeason = () => {
    const month = new Date().getMonth();
    if (month >= 2 && month <= 4) return "spring";
    if (month >= 5 && month <= 7) return "summer";
    if (month >= 8 && month <= 10) return "fall";
    return "winter";
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return "#28a745";
    if (confidence >= 0.6) return "#ffc107";
    return "#dc3545";
  };

  const getExperienceIcon = (level) => {
    const icons = {
      beginner: "🌱",
      intermediate: "⭐",
      advanced: "🏆",
      expert: "👑",
    };
    return icons[level] || icons.intermediate;
  };

  return (
    <div className={`recommendation-engine ${compact ? "compact" : ""}`}>
      {/* Header */}
      <div className="recommendation-header">
        <h3 className="recommendation-title">🎯 AI Personal Recommendations</h3>
        {!compact && (
          <p className="recommendation-subtitle">
            Discover beverages tailored to your unique taste preferences
          </p>
        )}
      </div>

      {/* Personalization Controls */}
      {showPersonalization && (
        <div className="personalization-controls">
          <button
            className="preferences-button"
            onClick={() => setShowPreferencesModal(true)}
          >
            ⚙️ Customize Preferences
          </button>

          {userPreferences.experienceLevel && (
            <div className="experience-badge">
              {getExperienceIcon(userPreferences.experienceLevel)}
              <span>{userPreferences.experienceLevel}</span>
            </div>
          )}

          <button
            className="refresh-button"
            onClick={() => generateRecommendations()}
            disabled={loading}
          >
            {loading ? "⏳" : "🔄"} Refresh
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="recommendation-loading">
          <div className="loading-spinner"></div>
          <p>
            Analyzing your preferences and generating personalized
            recommendations...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="recommendation-error">
          <p>{error}</p>
          <button
            onClick={() => generateRecommendations()}
            className="retry-button"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Recommendations */}
      {recommendations && (
        <div className="recommendations-container">
          {/* Profile Summary */}
          {recommendations.profile && !compact && (
            <div className="profile-summary">
              <h4>Your Taste Profile</h4>
              <div className="profile-tags">
                {recommendations.profile.dominantFlavors?.map((flavor) => (
                  <span key={flavor} className="profile-tag flavor">
                    {flavor}
                  </span>
                ))}
                {recommendations.profile.preferredCategories?.map(
                  (category) => (
                    <span key={category} className="profile-tag category">
                      {category}
                    </span>
                  )
                )}
              </div>
              <div className="confidence-indicator">
                <span>Profile Confidence: </span>
                <div
                  className="confidence-bar"
                  style={{
                    background: getConfidenceColor(recommendations.confidence),
                    width: `${recommendations.confidence * 100}%`,
                  }}
                />
                <span>{Math.round(recommendations.confidence * 100)}%</span>
              </div>
            </div>
          )}

          {/* Recommendations Grid */}
          <div className="recommendations-grid">
            {recommendations.recommendations.map((recommendation, index) => (
              <button
                key={recommendation.beverage?.id || `rec-${index}`}
                className={`recommendation-card ${
                  selectedRecommendation?.beverage?.id ===
                  recommendation.beverage?.id
                    ? "selected"
                    : ""
                }`}
                onClick={() => handleRecommendationClick(recommendation)}
                onKeyPress={(e) =>
                  e.key === "Enter" && handleRecommendationClick(recommendation)
                }
                type="button"
                aria-label={`Select recommendation: ${
                  recommendation.beverage?.name || recommendation.name
                }`}
              >
                <div className="card-header">
                  <div className="recommendation-info">
                    <h5 className="recommendation-name">
                      {recommendation.beverage?.name || recommendation.name}
                    </h5>
                    <p className="recommendation-description">
                      {recommendation.beverage?.description ||
                        recommendation.description}
                    </p>
                    {recommendation.beverage?.price && (
                      <span className="recommendation-price">
                        ${recommendation.beverage.price}
                      </span>
                    )}
                  </div>

                  <div className="recommendation-score">
                    <div
                      className="score-circle"
                      style={{
                        background: getConfidenceColor(
                          recommendation.confidence
                        ),
                      }}
                    >
                      {Math.round(recommendation.confidence * 100)}%
                    </div>
                    <span className="score-label">Match</span>
                  </div>
                </div>

                {/* Reasoning */}
                {recommendation.reasoning && (
                  <div className="recommendation-reasoning">
                    <p>{recommendation.reasoning}</p>
                  </div>
                )}

                {/* Tags */}
                <div className="recommendation-tags">
                  {recommendation.beverage?.category && (
                    <span className="rec-tag category">
                      {recommendation.beverage.category}
                    </span>
                  )}
                  {recommendation.matchType && (
                    <span className="rec-tag match-type">
                      {recommendation.matchType}
                    </span>
                  )}
                  {recommendation.beverage?.aiEnhanced && (
                    <span className="rec-tag ai-enhanced">AI Enhanced</span>
                  )}
                </div>

                {/* Feedback Buttons */}
                {!feedbackGiven.has(recommendation.beverage?.id) && (
                  <div className="feedback-buttons">
                    <button
                      className="feedback-btn like"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFeedback(recommendation.beverage, "like");
                      }}
                      title="I like this recommendation"
                    >
                      👍
                    </button>
                    <button
                      className="feedback-btn dislike"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFeedback(recommendation.beverage, "dislike");
                      }}
                      title="This doesn't match my taste"
                    >
                      👎
                    </button>
                  </div>
                )}

                {feedbackGiven.has(recommendation.beverage?.id) && (
                  <div className="feedback-received">
                    ✅ Thanks for your feedback!
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Learning Progress */}
          {!compact && (
            <div className="learning-progress">
              <h4>🧠 Learning Your Preferences</h4>
              <p>
                As you provide feedback, our AI learns your taste preferences to
                make even better recommendations. Keep exploring!
              </p>
              <div className="interaction-stats">
                <span>Feedback given: {feedbackGiven.size}</span>
                <span>•</span>
                <span>
                  Recommendations generated:{" "}
                  {recommendations.recommendations.length}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferencesModal && (
        <PreferencesModal
          preferences={userPreferences}
          onSave={handlePreferenceUpdate}
          onClose={() => setShowPreferencesModal(false)}
        />
      )}

      {/* AI Disclaimer */}
      {!compact && (
        <div className="recommendation-footer">
          <p className="ai-disclaimer">
            Recommendations powered by AI analysis of your preferences, flavor
            profiles, and learning algorithms
          </p>
        </div>
      )}
    </div>
  );
};

// Preferences Modal Component
const PreferencesModal = ({ preferences, onSave, onClose }) => {
  const [localPreferences, setLocalPreferences] = useState(preferences);

  const handleSave = () => {
    onSave(localPreferences);
  };

  const updatePreference = (key, value) => {
    setLocalPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleArrayItem = (key, item) => {
    setLocalPreferences((prev) => ({
      ...prev,
      [key]: prev[key].includes(item)
        ? prev[key].filter((i) => i !== item)
        : [...prev[key], item],
    }));
  };

  return (
    <div className="preferences-modal-overlay">
      <div className="preferences-modal">
        <div className="modal-header">
          <h3 id="preferences-modal-title">
            🎯 Customize Your Taste Preferences
          </h3>
          <button
            className="close-button"
            onClick={onClose}
            aria-label="Close preferences modal"
          >
            ×
          </button>
        </div>

        <div className="modal-content">
          {/* Experience Level */}
          <div className="preference-group">
            <label htmlFor="experience-level-select">Experience Level</label>
            <select
              id="experience-level-select"
              value={localPreferences.experienceLevel}
              onChange={(e) =>
                updatePreference("experienceLevel", e.target.value)
              }
            >
              <option value="beginner">🌱 Beginner</option>
              <option value="intermediate">⭐ Intermediate</option>
              <option value="advanced">🏆 Advanced</option>
              <option value="expert">👑 Expert</option>
            </select>
          </div>

          {/* Favorite Flavors */}
          <fieldset className="preference-group">
            <legend>Favorite Flavors</legend>
            <div className="preference-chips">
              {[
                "Fruity",
                "Earthy",
                "Spicy",
                "Sweet",
                "Dry",
                "Bold",
                "Light",
                "Crisp",
                "Smooth",
                "Complex",
              ].map((flavor) => (
                <button
                  key={flavor}
                  className={`preference-chip ${
                    localPreferences.flavors.includes(flavor) ? "selected" : ""
                  }`}
                  onClick={() => toggleArrayItem("flavors", flavor)}
                >
                  {flavor}
                </button>
              ))}
            </div>
          </fieldset>

          {/* Preferred Categories */}
          <fieldset className="preference-group">
            <legend>Preferred Categories</legend>
            <div className="preference-chips">
              {["Wine", "Beer", "Spirits", "Cocktails", "Non-Alcoholic"].map(
                (category) => (
                  <button
                    key={category}
                    className={`preference-chip ${
                      localPreferences.categories.includes(category)
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleArrayItem("categories", category)}
                  >
                    {category}
                  </button>
                )
              )}
            </div>
          </fieldset>

          {/* Price Range */}
          <div className="preference-group">
            <label htmlFor="price-range-min">Price Range</label>
            <div className="price-range">
              <input
                id="price-range-min"
                type="number"
                placeholder="Min $"
                value={localPreferences.priceRange.min}
                onChange={(e) =>
                  updatePreference("priceRange", {
                    ...localPreferences.priceRange,
                    min: e.target.value,
                  })
                }
              />
              <span>to</span>
              <input
                id="price-range-max"
                type="number"
                placeholder="Max $"
                value={localPreferences.priceRange.max}
                onChange={(e) =>
                  updatePreference("priceRange", {
                    ...localPreferences.priceRange,
                    max: e.target.value,
                  })
                }
                aria-label="Maximum price"
              />
            </div>
          </div>

          {/* Personal Notes */}
          <div className="preference-group">
            <label htmlFor="personal-notes">Personal Notes</label>
            <textarea
              id="personal-notes"
              placeholder="Tell us more about your preferences, favorite occasions, or anything else that helps us understand your taste..."
              value={localPreferences.personalNotes}
              onChange={(e) =>
                updatePreference("personalNotes", e.target.value)
              }
              rows={3}
            />
          </div>
        </div>

        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button className="save-button" onClick={handleSave}>
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

RecommendationEngine.propTypes = {
  inventory: PropTypes.array,
  compact: PropTypes.bool,
  maxRecommendations: PropTypes.number,
  showPersonalization: PropTypes.bool,
  onRecommendationSelect: PropTypes.func,
};

PreferencesModal.propTypes = {
  preferences: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RecommendationEngine;
